import { c as _c } from "react/compiler-runtime";
import type { AgentGraphOutputs, OrgArtifact } from '@onefootprint/request-types/dashboard';
import { SearchInput } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ArtifactSelectorOptionId, GENERATED_SUMMARY_OPTION_ID } from '../header/types';
import { type ArtifactPlanTask, artifactLabel, FlatArtifactList, GroupedArtifactList } from './artifact-list';
import FinalOutput from './final-output';
export type { ArtifactPlanTask };
const isJudgement = (artifact: OrgArtifact) => artifact.metadata.kind === 'agent_graph_judgement';

// Agent artifacts, judgments, rule results, and decision results carry the id of the graph node that
// produced them; other kinds (user uploads, blank drafts) have no producing node, so they can't group
// under a task.
const nodeIdOf = (artifact: OrgArtifact): string | null => artifact.metadata.kind === 'agent_graph_artifact' || artifact.metadata.kind === 'agent_graph_judgement' || artifact.metadata.kind === 'agent_graph_rule_result' || artifact.metadata.kind === 'agent_graph_decision_result' ? artifact.metadata.data.nodeId : null;
type ArtifactSidebarProps = {
  artifacts: OrgArtifact[];
  selectedOptionId?: ArtifactSelectorOptionId;
  onSelect: (optionId: ArtifactSelectorOptionId) => void;
  onBlankDocument: () => void;
  /** While a markdown artifact has unsaved edits, switching is disabled so dirty work can't be silently dropped. */
  disabled?: boolean;
  open?: boolean;
  /** When set, group artifacts under their producing task node (in plan order) instead of the flat
   * Artifacts/Agent-judgments split. */
  plan?: ArtifactPlanTask[];
  /** Scalar judgment values by artifact id, shown as a tag on the matching judgment rows. */
  judgmentValues?: Record<string, string>;
  outputs?: AgentGraphOutputs;
};
const ArtifactSidebar = t0 => {
  const $ = _c(64);
  const {
    artifacts: t1,
    selectedOptionId: t2,
    onSelect,
    onBlankDocument,
    disabled: t3,
    open: t4,
    plan,
    judgmentValues,
    outputs
  } = t0;
  const artifacts = t1 === undefined ? [] : t1;
  const selectedOptionId = t2 === undefined ? GENERATED_SUMMARY_OPTION_ID : t2;
  const disabled = t3 === undefined ? false : t3;
  const open = t4 === undefined ? true : t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t5);
  const [query, setQuery] = useState("");
  const selectableArtifacts = artifacts.filter(_temp);
  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;
  const matches = label => !isSearching || label.toLowerCase().includes(q);
  const byLabel = _temp2;
  const inputsArtifact = selectableArtifacts.find(_temp3);
  const listArtifacts = selectableArtifacts.filter(_temp4);
  const generatedSummaryLabel = t(_temp5);
  const showGeneratedSummary = selectableArtifacts.length === 0 || selectedOptionId === GENERATED_SUMMARY_OPTION_ID;
  const summaryMatches = showGeneratedSummary && matches(generatedSummaryLabel);
  const inputsMatches = !!inputsArtifact && matches(t(_temp6));
  const judgements = listArtifacts.filter(a_3 => isJudgement(a_3) && matches(artifactLabel(a_3))).sort(byLabel);
  const files = listArtifacts.filter(a_4 => !isJudgement(a_4) && matches(artifactLabel(a_4))).sort(byLabel);
  const showDocuments = summaryMatches || files.length > 0;
  let t6;
  if ($[1] !== plan) {
    t6 = new Set((plan ?? []).map(_temp7));
    $[1] = plan;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const producedNodeIds = t6;
  const taskGroups = (plan ?? []).map(task_0 => ({
    task: task_0,
    items: listArtifacts.filter(a_5 => nodeIdOf(a_5) === task_0.nodeId && matches(artifactLabel(a_5))).sort(byLabel)
  })).filter(_temp8);
  const otherArtifacts = listArtifacts.filter(a_6 => {
    const nodeId = nodeIdOf(a_6);
    return (nodeId === null || !producedNodeIds.has(nodeId)) && matches(artifactLabel(a_6));
  }).sort(byLabel);
  const hasResults = plan ? inputsMatches || taskGroups.length > 0 || otherArtifacts.length > 0 || summaryMatches : inputsMatches || showDocuments || judgements.length > 0;
  const panelWidth = plan ? "w-[328px]" : "w-64";
  const t7 = inputsMatches ? inputsArtifact : undefined;
  let t8;
  if ($[3] !== generatedSummaryLabel || $[4] !== judgmentValues || $[5] !== onBlankDocument || $[6] !== onSelect || $[7] !== selectedOptionId || $[8] !== summaryMatches || $[9] !== t7) {
    t8 = {
      selectedOptionId,
      onSelect,
      onBlankDocument,
      summaryMatches,
      generatedSummaryLabel,
      judgmentValues,
      inputsArtifact: t7
    };
    $[3] = generatedSummaryLabel;
    $[4] = judgmentValues;
    $[5] = onBlankDocument;
    $[6] = onSelect;
    $[7] = selectedOptionId;
    $[8] = summaryMatches;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const commonListProps = t8;
  let t9;
  if ($[11] !== commonListProps || $[12] !== files || $[13] !== hasResults || $[14] !== isSearching || $[15] !== judgements || $[16] !== otherArtifacts || $[17] !== plan || $[18] !== query || $[19] !== showDocuments || $[20] !== t || $[21] !== taskGroups) {
    t9 = () => {
      if (isSearching && !hasResults) {
        return <output aria-live="polite" className="block px-2 py-8 text-center text-body-3 text-tertiary">{t(_temp9, {
            query: query.trim()
          })}</output>;
      }
      if (plan) {
        return <GroupedArtifactList taskGroups={taskGroups} otherArtifacts={otherArtifacts} forceOpen={isSearching} {...commonListProps} />;
      }
      return <FlatArtifactList showDocuments={showDocuments} files={files} judgements={judgements} forceOpen={isSearching} {...commonListProps} />;
    };
    $[11] = commonListProps;
    $[12] = files;
    $[13] = hasResults;
    $[14] = isSearching;
    $[15] = judgements;
    $[16] = otherArtifacts;
    $[17] = plan;
    $[18] = query;
    $[19] = showDocuments;
    $[20] = t;
    $[21] = taskGroups;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const renderBody = t9;
  let t10;
  if ($[23] !== t) {
    t10 = t(_temp10);
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== open || $[26] !== panelWidth) {
    t11 = cx("max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-10 max-md:max-w-full shrink-0 h-full flex flex-col bg-primary overflow-hidden transition-[width] duration-200 ease-in-out", open ? cx(panelWidth, "border-r border-tertiary") : "w-0");
    $[25] = open;
    $[26] = panelWidth;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = disabled || !open;
  const t13 = disabled && "opacity-60";
  let t14;
  if ($[28] !== panelWidth || $[29] !== t13) {
    t14 = cx(panelWidth, "h-full flex flex-col", t13);
    $[28] = panelWidth;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== setQuery) {
    t15 = () => setQuery("");
    $[31] = setQuery;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t(_temp11);
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t(_temp12);
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== query || $[38] !== setQuery || $[39] !== t15 || $[40] !== t16 || $[41] !== t17) {
    t18 = <div className="shrink-0 px-3 pt-3 pb-2"><SearchInput value={query} onChangeText={setQuery} onReset={t15} placeholder={t16} aria-label={t17} size="compact" /></div>;
    $[37] = query;
    $[38] = setQuery;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== renderBody) {
    t19 = renderBody();
    $[43] = renderBody;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== artifacts || $[46] !== isSearching || $[47] !== judgmentValues || $[48] !== onSelect || $[49] !== outputs || $[50] !== selectedOptionId) {
    t20 = !isSearching && <FinalOutput artifacts={artifacts} outputs={outputs} selectedOptionId={selectedOptionId} onSelect={onSelect} judgmentValues={judgmentValues} />;
    $[45] = artifacts;
    $[46] = isSearching;
    $[47] = judgmentValues;
    $[48] = onSelect;
    $[49] = outputs;
    $[50] = selectedOptionId;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t19 || $[53] !== t20) {
    t21 = <div className="flex-1 overflow-y-auto px-3 pt-2 pb-20 md:pb-4 flex flex-col gap-2">{t19}{t20}</div>;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t12 || $[56] !== t14 || $[57] !== t18 || $[58] !== t21) {
    t22 = <div inert={t12} className={t14}>{t18}{t21}</div>;
    $[55] = t12;
    $[56] = t14;
    $[57] = t18;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t10 || $[61] !== t11 || $[62] !== t22) {
    t23 = <aside aria-label={t10} className={t11}>{t22}</aside>;
    $[60] = t10;
    $[61] = t11;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  return t23;
};
export default ArtifactSidebar;
function _temp(a) {
  return a.metadata.kind !== "ui_block";
}
function _temp2(a_0, b) {
  return artifactLabel(a_0).localeCompare(artifactLabel(b));
}
function _temp3(a_1) {
  return a_1.metadata.kind === "agent_graph_inputs";
}
function _temp4(a_2) {
  return a_2.metadata.kind !== "agent_graph_inputs";
}
function _temp5($) {
  return $.generatedSummary;
}
function _temp6($_0) {
  return $_0.inputs;
}
function _temp7(task) {
  return task.nodeId;
}
function _temp8(group) {
  return group.items.length > 0;
}
function _temp9($_1) {
  return $_1.search.noResults;
}
function _temp10($_2) {
  return $_2.ariaLabel;
}
function _temp11($_3) {
  return $_3.search.placeholder;
}
function _temp12($_4) {
  return $_4.search.ariaLabel;
}